import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { canAccess } from "@/lib/roles";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Students = lazy(() => import("@/pages/Students"));
const StudentForm = lazy(() => import("@/pages/StudentForm"));
const StudentDetail = lazy(() => import("@/pages/StudentDetail"));
const MyRecord = lazy(() => import("@/pages/MyRecord"));
const SPP = lazy(() => import("@/pages/SPP"));
const Transport = lazy(() => import("@/pages/Transport"));
const Users = lazy(() => import("@/pages/Users"));
const Admins = lazy(() => import("@/pages/Admins"));
const Integrations = lazy(() => import("@/pages/Integrations"));
const Kartu = lazy(() => import("@/pages/Kartu"));
const StudentPrint = lazy(() => import("@/pages/StudentPrint"));
const CardPrint = lazy(() => import("@/pages/CardPrint"));
const Laporan = lazy(() => import("@/pages/Laporan"));
const Kwitansi = lazy(() => import("@/pages/Kwitansi"));
const KwitansiGabungan = lazy(() => import("@/pages/KwitansiGabungan"));
const KwitansiMassal = lazy(() => import("@/pages/KwitansiMassal"));
const KwitansiTransport = lazy(() => import("@/pages/KwitansiTransport"));
const KwitansiTransportMassal = lazy(() => import("@/pages/KwitansiTransportMassal"));
const LaporanTransport = lazy(() => import("@/pages/LaporanTransport"));
const BukuKas = lazy(() => import("@/pages/BukuKas"));
const PapanTransaksi = lazy(() => import("@/pages/PapanTransaksi"));
const PesertaTransport = lazy(() => import("@/pages/PesertaTransport"));
const CatatanKelas = lazy(() => import("@/pages/CatatanKelas"));
const JurnalMengajar = lazy(() => import("@/pages/JurnalMengajar"));
const TugasSaya = lazy(() => import("@/pages/TugasSaya"));
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const RekapKelas = lazy(() => import("@/pages/RekapKelas"));
const RekapTransportPrint = lazy(() => import("@/pages/RekapTransportPrint"));
const LibraryBooks = lazy(() => import("@/pages/LibraryBooks"));
const LibraryLoans = lazy(() => import("@/pages/LibraryLoans"));
const LibraryReport = lazy(() => import("@/pages/LibraryReport"));
const KunjunganPerpustakaan = lazy(() => import("@/pages/KunjunganPerpustakaan"));
const PerpustakaanKiosk = lazy(() => import("@/pages/PerpustakaanKiosk"));
const LibraryReportPrint = lazy(() => import("@/pages/LibraryReportPrint"));
const RekapSPPPrint = lazy(() => import("@/pages/RekapSPPPrint"));
const Settings = lazy(() => import("@/pages/Settings"));
const Konseling = lazy(() => import("@/pages/Konseling"));
const KonselorSiswa = lazy(() => import("@/pages/KonselorSiswa"));
const Alumni = lazy(() => import("@/pages/Alumni"));
const AkunSaya = lazy(() => import("@/pages/AkunSaya"));
const Presensi = lazy(() => import("@/pages/Presensi"));
const Akademik = lazy(() => import("@/pages/Akademik"));
const AkademikSiswa = lazy(() => import("@/pages/AkademikSiswa"));
const NilaiSaya = lazy(() => import("@/pages/NilaiSaya"));
const CatatanSaya = lazy(() => import("@/pages/CatatanSaya"));
const Inventaris = lazy(() => import("@/pages/Inventaris"));
const AsetBMN = lazy(() => import("@/pages/AsetBMN"));
const Beasiswa = lazy(() => import("@/pages/Beasiswa"));
const InventarisKiosk = lazy(() => import("@/pages/InventarisKiosk"));
const PresensiKiosk = lazy(() => import("@/pages/PresensiKiosk"));
const PresensiHarian = lazy(() => import("@/pages/PresensiHarian"));
const KartuSaya = lazy(() => import("@/pages/KartuSaya"));
const DataMahasiswa = lazy(() => import("@/pages/DataMahasiswa"));
const JurnalMahasiswa = lazy(() => import("@/pages/JurnalMahasiswa"));
const MahasiswaKkn = lazy(() => import("@/pages/MahasiswaKkn"));
const MahasiswaCardPrint = lazy(() => import("@/pages/MahasiswaCardPrint"));
const IzinSaya = lazy(() => import("@/pages/IzinSaya"));
const VerifikasiIzin = lazy(() => import("@/pages/VerifikasiIzin"));
const TugasIzin = lazy(() => import("@/pages/TugasIzin"));
const PresensiSaya = lazy(() => import("@/pages/PresensiSaya"));
const Gtk = lazy(() => import("@/pages/Gtk"));
const GtkCardPrint = lazy(() => import("@/pages/GtkCardPrint"));
const LabelPrint = lazy(() => import("@/pages/LabelPrint"));
const KatalogBuku = lazy(() => import("@/pages/KatalogBuku"));
const KenaikanKelas = lazy(() => import("@/pages/KenaikanKelas"));
const KaryaSaya = lazy(() => import("@/pages/KaryaSaya"));
const VerifikasiKarya = lazy(() => import("@/pages/VerifikasiKarya"));
const JurnalKonselor = lazy(() => import("@/pages/JurnalKonselor"));
const JurnalKelas = lazy(() => import("@/pages/JurnalKelas"));
const MonitorKelas = lazy(() => import("@/pages/MonitorKelas"));
const LaporanSaya = lazy(() => import("@/pages/LaporanSaya"));
const CutiGtk = lazy(() => import("@/pages/CutiGtk"));
const KoperasiGTK = lazy(() => import("@/pages/KoperasiGTK"));
const KoperasiSaya = lazy(() => import("@/pages/KoperasiSaya"));

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400 font-mono text-sm">
        Memuat...
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && location.pathname !== "/akun") return <Navigate to="/akun" replace />;
  if (!canAccess(user, location.pathname, roles)) return <Navigate to="/" replace />;
  return children;
}

const Fallback = () => <div className="min-h-[40vh] flex items-center justify-center text-slate-400 font-mono text-sm">Memuat...</div>;

function AppRoutes() {
  return (
    <Suspense fallback={<Fallback />}>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/cetak/siswa/:id"
        element={
          <Protected>
            <StudentPrint />
          </Protected>
        }
      />
      <Route
        path="/cetak/kartu"
        element={
          <Protected roles={["super_admin", "guru", "bendahara", "operator"]}>
            <CardPrint />
          </Protected>
        }
      />
      <Route
        path="/cetak/label-atk"
        element={
          <Protected roles={["super_admin", "operator", "pustakawan"]}>
            <LabelPrint />
          </Protected>
        }
      />
      <Route
        path="/cetak/kartu-gtk"
        element={
          <Protected roles={["super_admin", "operator"]}>
            <GtkCardPrint />
          </Protected>
        }
      />
      <Route
        path="/cetak/kartu-mahasiswa"
        element={
          <Protected roles={["super_admin", "operator", "wakil_kurikulum"]}>
            <MahasiswaCardPrint />
          </Protected>
        }
      />
      <Route
        path="/kiosk/presensi"
        element={
          <Protected roles={["super_admin", "operator", "piket"]}>
            <PresensiKiosk />
          </Protected>
        }
      />
      <Route
        path="/kiosk/inventaris"
        element={
          <Protected roles={["super_admin", "operator"]}>
            <InventarisKiosk />
          </Protected>
        }
      />
      <Route
        path="/kiosk/perpustakaan"
        element={
          <Protected roles={["super_admin", "pustakawan"]}>
            <PerpustakaanKiosk />
          </Protected>
        }
      />
      <Route
        path="/cetak/kwitansi/:id"
        element={
          <Protected>
            <Kwitansi />
          </Protected>
        }
      />
      <Route
        path="/cetak/kwitansi-gabungan"
        element={
          <Protected>
            <KwitansiGabungan />
          </Protected>
        }
      />
      <Route
        path="/cetak/kwitansi-transport/:id"
        element={
          <Protected>
            <KwitansiTransport />
          </Protected>
        }
      />
      <Route
        path="/cetak/rekap-transport"
        element={
          <Protected roles={["super_admin", "bendahara", "guru"]}>
            <RekapTransportPrint />
          </Protected>
        }
      />
      <Route
        path="/cetak/kwitansi-transport-massal"
        element={
          <Protected roles={["super_admin", "bendahara"]}>
            <KwitansiTransportMassal />
          </Protected>
        }
      />
      <Route
        path="/cetak/rekap-spp"
        element={
          <Protected roles={["super_admin", "bendahara", "guru"]}>
            <RekapSPPPrint />
          </Protected>
        }
      />
      <Route
        path="/cetak/laporan-perpustakaan"
        element={
          <Protected roles={["super_admin", "pustakawan"]}>
            <LibraryReportPrint />
          </Protected>
        }
      />
      <Route
        path="/cetak/kwitansi-massal"
        element={
          <Protected roles={["super_admin", "bendahara"]}>
            <KwitansiMassal />
          </Protected>
        }
      />
      <Route
        path="/"
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route index element={<Dashboard />} />
        <Route
          path="siswa"
          element={
            <Protected roles={["super_admin", "guru", "bendahara", "operator"]}>
              <Students />
            </Protected>
          }
        />
        <Route
          path="siswa/baru"
          element={
            <Protected roles={["super_admin", "guru", "bendahara", "operator"]}>
              <StudentForm />
            </Protected>
          }
        />
        <Route
          path="siswa/:id/edit"
          element={
            <Protected roles={["super_admin", "guru", "bendahara", "operator", "konselor"]}>
              <StudentForm />
            </Protected>
          }
        />
        <Route
          path="siswa/:id"
          element={
            <Protected roles={["super_admin", "guru", "bendahara", "operator"]}>
              <StudentDetail />
            </Protected>
          }
        />
        <Route
          path="gtk"
          element={
            <Protected roles={["super_admin", "operator"]}>
              <Gtk />
            </Protected>
          }
        />
        <Route
          path="kartu-gtk-saya"
          element={
            <Protected roles={["super_admin", "guru", "bendahara", "pustakawan", "operator", "konselor"]}>
              <KartuSaya gtk />
            </Protected>
          }
        />
        <Route
          path="presensi-gtk-saya"
          element={
            <Protected roles={["super_admin", "guru", "bendahara", "pustakawan", "operator", "konselor"]}>
              <PresensiSaya gtk />
            </Protected>
          }
        />
        <Route
          path="presensi-saya"
          element={
            <Protected roles={["siswa"]}>
              <PresensiSaya />
            </Protected>
          }
        />
        <Route
          path="data-mahasiswa"
          element={
            <Protected roles={["mahasiswa"]}>
              <DataMahasiswa />
            </Protected>
          }
        />
        <Route
          path="kartu-mahasiswa"
          element={
            <Protected roles={["mahasiswa"]}>
              <KartuSaya mahasiswa />
            </Protected>
          }
        />
        <Route
          path="jurnal-mahasiswa"
          element={
            <Protected roles={["mahasiswa"]}>
              <JurnalMahasiswa />
            </Protected>
          }
        />
        <Route
          path="mahasiswa-kkn"
          element={
            <Protected roles={["super_admin", "operator", "wakil_kurikulum"]}>
              <MahasiswaKkn />
            </Protected>
          }
        />
        <Route
          path="izin"
          element={
            <Protected roles={["guru"]}>
              <IzinSaya />
            </Protected>
          }
        />
        <Route
          path="verifikasi-izin"
          element={
            <Protected roles={["super_admin", "wakil_sdm"]}>
              <VerifikasiIzin />
            </Protected>
          }
        />
        <Route
          path="tugas-izin"
          element={
            <Protected roles={["super_admin", "wakil_sdm", "wakil_kurikulum", "piket", "kepala_sekolah"]}>
              <TugasIzin />
            </Protected>
          }
        />
        <Route
          path="kartu-saya"
          element={
            <Protected roles={["siswa"]}>
              <KartuSaya />
            </Protected>
          }
        />
        <Route
          path="data-saya"
          element={
            <Protected roles={["siswa"]}>
              <MyRecord />
            </Protected>
          }
        />
        <Route
          path="spp"
          element={
            <Protected roles={["super_admin", "bendahara", "siswa"]}>
              <SPP />
            </Protected>
          }
        />
        <Route
          path="transport"
          element={
            <Protected roles={["super_admin", "bendahara", "siswa"]}>
              <Transport />
            </Protected>
          }
        />
        <Route
          path="catatan-kelas"
          element={
            <Protected roles={["super_admin", "operator", "guru"]}>
              <CatatanKelas />
            </Protected>
          }
        />
        <Route
          path="peserta-transport"
          element={
            <Protected roles={["super_admin", "bendahara", "guru"]}>
              <PesertaTransport />
            </Protected>
          }
        />
        <Route
          path="buku-kas"
          element={
            <Protected roles={["super_admin", "bendahara"]}>
              <BukuKas />
            </Protected>
          }
        />
        <Route
          path="papan-transaksi"
          element={
            <Protected roles={["super_admin", "bendahara", "kepala_sekolah", "komite", "maker", "checker", "approver"]}>
              <PapanTransaksi />
            </Protected>
          }
        />
        <Route
          path="koperasi"
          element={
            <Protected roles={["super_admin", "bendahara"]}>
              <KoperasiGTK />
            </Protected>
          }
        />
        <Route
          path="beasiswa"
          element={
            <Protected roles={["super_admin", "bendahara"]}>
              <Beasiswa />
            </Protected>
          }
        />
        <Route
          path="koperasi-saya"
          element={
            <Protected roles={["guru", "bendahara", "pustakawan", "operator", "konselor", "piket"]}>
              <KoperasiSaya />
            </Protected>
          }
        />
        <Route
          path="laporan-transport"
          element={
            <Protected roles={["super_admin", "guru", "bendahara"]}>
              <LaporanTransport />
            </Protected>
          }
        />
        <Route
          path="rekap-kelas"
          element={
            <Protected roles={["super_admin", "guru", "bendahara"]}>
              <RekapKelas />
            </Protected>
          }
        />
        <Route
          path="alumni"
          element={
            <Protected>
              <Alumni />
            </Protected>
          }
        />
        <Route
          path="akun"
          element={
            <Protected>
              <AkunSaya />
            </Protected>
          }
        />
        <Route
          path="presensi-harian"
          element={
            <Protected roles={["super_admin", "operator", "wakil_kurikulum"]}>
              <PresensiHarian />
            </Protected>
          }
        />
        <Route
          path="presensi"
          element={
            <Protected roles={["super_admin", "guru"]}>
              <Presensi />
            </Protected>
          }
        />
        <Route
          path="akademik"
          element={
            <Protected roles={["super_admin", "guru", "operator", "konselor", "bendahara"]}>
              <Akademik />
            </Protected>
          }
        />
        <Route
          path="akademik/:id"
          element={
            <Protected roles={["super_admin", "guru", "operator", "konselor", "bendahara"]}>
              <AkademikSiswa />
            </Protected>
          }
        />
        <Route
          path="nilai-saya"
          element={
            <Protected roles={["siswa"]}>
              <NilaiSaya />
            </Protected>
          }
        />
        <Route
          path="jurnal"
          element={
            <Protected roles={["super_admin", "operator", "guru"]}>
              <JurnalMengajar />
            </Protected>
          }
        />
        <Route
          path="tugas-saya"
          element={
            <Protected roles={["siswa"]}>
              <TugasSaya />
            </Protected>
          }
        />
        <Route
          path="catatan-wali-saya"
          element={
            <Protected roles={["siswa"]}>
              <CatatanSaya />
            </Protected>
          }
        />
        <Route
          path="inventaris"
          element={
            <Protected roles={["super_admin", "operator"]}>
              <Inventaris />
            </Protected>
          }
        />
        <Route
          path="aset"
          element={
            <Protected roles={["super_admin", "operator", "wakil_sarpras", "kepala_sekolah"]}>
              <AsetBMN />
            </Protected>
          }
        />
        <Route
          path="katalog-buku"
          element={
            <Protected roles={["siswa", "guru", "super_admin", "pustakawan", "konselor", "operator", "bendahara"]}>
              <KatalogBuku />
            </Protected>
          }
        />
        <Route
          path="cuti-gtk"
          element={
            <Protected roles={["super_admin", "wakil_sdm", "kepala_sekolah", "wakil_kurikulum", "operator"]}>
              <CutiGtk />
            </Protected>
          }
        />
        <Route
          path="jurnal-kelas"
          element={
            <Protected roles={["ketua_kelas", "piket", "super_admin"]}>
              <JurnalKelas />
            </Protected>
          }
        />
        <Route
          path="monitor-kelas"
          element={
            <Protected roles={["super_admin", "wakil_kurikulum", "kepala_sekolah", "operator", "piket"]}>
              <MonitorKelas />
            </Protected>
          }
        />
        <Route
          path="laporan-saya"
          element={
            <Protected roles={["guru"]}>
              <LaporanSaya />
            </Protected>
          }
        />
        <Route
          path="jurnal-konselor"
          element={
            <Protected roles={["konselor"]}>
              <JurnalKonselor />
            </Protected>
          }
        />
        <Route
          path="karya-saya"
          element={
            <Protected roles={["siswa", "guru", "kepala_sekolah", "pustakawan"]}>
              <KaryaSaya />
            </Protected>
          }
        />
        <Route
          path="verifikasi-karya"
          element={
            <Protected roles={["super_admin", "pustakawan"]}>
              <VerifikasiKarya />
            </Protected>
          }
        />
        <Route
          path="kenaikan-kelas"
          element={
            <Protected roles={["super_admin", "operator"]}>
              <KenaikanKelas />
            </Protected>
          }
        />
        <Route
          path="konseling"
          element={
            <Protected roles={["konselor", "siswa"]}>
              <Konseling />
            </Protected>
          }
        />
        <Route
          path="lihat-siswa"
          element={
            <Protected roles={["konselor"]}>
              <KonselorSiswa />
            </Protected>
          }
        />
        <Route
          path="log-aktivitas"
          element={
            <Protected roles={["super_admin"]}>
              <AuditLog />
            </Protected>
          }
        />
        <Route
          path="pengguna"
          element={
            <Protected roles={["super_admin"]}>
              <Users />
            </Protected>
          }
        />
        <Route
          path="admin"
          element={
            <Protected roles={["super_admin"]}>
              <Admins />
            </Protected>
          }
        />
        <Route
          path="kartu"
          element={
            <Protected roles={["super_admin", "guru", "bendahara", "operator"]}>
              <Kartu />
            </Protected>
          }
        />
        <Route
          path="laporan"
          element={
            <Protected roles={["super_admin", "guru", "bendahara"]}>
              <Laporan />
            </Protected>
          }
        />
        <Route
          path="buku"
          element={
            <Protected roles={["super_admin", "pustakawan"]}>
              <LibraryBooks />
            </Protected>
          }
        />
        <Route
          path="peminjaman"
          element={
            <Protected roles={["super_admin", "pustakawan"]}>
              <LibraryLoans />
            </Protected>
          }
        />
        <Route
          path="laporan-perpustakaan"
          element={
            <Protected roles={["super_admin", "pustakawan"]}>
              <LibraryReport />
            </Protected>
          }
        />
        <Route
          path="kunjungan-perpustakaan"
          element={
            <Protected roles={["super_admin", "pustakawan"]}>
              <KunjunganPerpustakaan />
            </Protected>
          }
        />
        <Route
          path="pengaturan"
          element={
            <Protected roles={["super_admin"]}>
              <Settings />
            </Protected>
          }
        />
        <Route
          path="integrasi"
          element={
            <Protected roles={["super_admin"]}>
              <Integrations />
            </Protected>
          }
        />
      </Route>
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
